import { BCMSDate } from "@bcm-dev/bcms-vite";
import { Document } from "model";

/**
 * @class StatementSearchItem
 * @classdesc Class definition for the statement search item
 * @param {OBJECT} values - the values used to set the properties of the item
 * @example const item = StatementSearchItem.init({});
 * 
 * @property {STRING} _account - The id of the selected account or "all" if the search should show all accounts
 * @property {STRING|BCMSDate} _startDate - The date used to check if documents are after the set date
 * @property {STRING|BCMSDate} _endDate - The date used to check if documents are before the set date
 * @property {STRING} _document - The document type
 * @property {STRING} _category - The category of the document item
 * @property {ARRAY} _filtered - Array of document items used by the table
 */
export default class StatementSearchItem {
    _account;
    _startDate;
    _endDate;
    _document;
    _category;
    _filtered;

    constructor({ account, startDate, endDate, document, category, filtered }) {
        this.account = account ?? "all";
        this.startDate = startDate ?? BCMSDate.init().offset([ 0, -6, 0 ]);
        this.endDate = endDate ?? BCMSDate.init();
        this.document = document ?? "all";
        this.category = category ?? "all";
        this.filtered = filtered ?? Document.sortDateAscending(Document.initAll());
    }

    /**
     * @function account
     * @instance
     * @param {STRING} value - The value to be set to the account property
     * @description The setter for the account property
     * @memberof StatementSearchItem
     */
    set account(value) { this._account = value; }
    /**
     * @function startDate
     * @instance
     * @param {STRING|BCMSDate} value - The value to be set for the setDate property
     * @description The setter for the startDate property
     * @memberof StatementSearchItem
     */
    set startDate(value) { this._startDate = value; }
    /**
     * @function endDate
     * @instance
     * @param {STRING|BCMSDate} value - The value to be set for the endDate property
     * @description The setter for the endDate property
     * @memberof StatementSearchItem
     */
    set endDate(value) { this._endDate = value; }
    /**
     * @function document
     * @instance
     * @param {STRING} value - The value to be set for the document property
     * @description The setter for the document property
     * @memberof StatementSearchItem
     */
    set document(value) { this._document = value; }
    /**
     * @function category
     * @instance
     * @param {STRING} value - The value to be set for the category property
     * @memberof StatementSearchItem
     */
    set category(value) { this._category = value; }
    /**
     * @function filtered
     * @instance
     * @param {ARRAY} value - The value to be set for the filtered property
     * @memberof StatementSearchItem
     */
    set filtered(value) { this._filtered = value; }

    /**
     * @function account
     * @instance
     * @returns {STRING}
     * @description Getter for the account property
     * @memberof StatementSearchItem
     */
    get account() { return this._account; }
    /**
     * @function startDate
     * @instance
     * @returns {STRING|BCMSDate}
     * @description Getter for the startDate property
     * @memberof StatementSearchItem
     */
    get startDate() { return this._startDate; }
    /**
     * @function endDate
     * @instance
     * @returns {STRING|BCMSDate}
     * @description Getter for the endDate property
     * @memberof StatementSearchItem
     */
    get endDate() { return this._endDate; }
    /**
     * @function document
     * @instance
     * @returns {STRING}
     * @description Getter for the document property
     * @memberof StatementSearchItem
     */
    get document() { return this._document; }
    /**
     * @function category
     * @instance
     * @returns {STRING}
     * @description Getter for the document property
     * @memberof StatementSearchItem
     */
    get category() { return this._category; }
    /**
     * @function filtered
     * @instance
     * @returns {ARRAY}
     * @description Getter for the fitlered property
     * @memberof StatementSearchItem
     */
    get filtered() { return this._filtered; }

    /**
     * @function record
     * @instance
     * @returns {OBJECT}
     * @description Getter to retrieve all the values from the class as an object
     * @memberof StatementSearchItem
     */
    get record() {
        return {
            account: this.account,
            startDate: this.startDate,
            endDate: this.endDate,
            document: this.document,
            category: this.category,
            filtered: this.filtered
        };
    }

    /**
     * @function reset
     * @instance
     * @returns {VOID}
     * @description Function to reset the values
     * @memberof StatementSearchItem
     */
    reset() {
        this.account = "all";
        this.startDate = BCMSDate.init().offset([ 0, -6, 0 ]);
        this.endDate = BCMSDate.init();
        this.document = "all";
        this.category = "all";
        this.filtered = Document.sortDateAscending(Document.initAll());

        return this;
    }

    /**
     * @function filter
     * @instance
     * @returns {VOID}
     * @description Function to set the filtered array based on the values set
     * @memberof StatementSearchItem
     */
    filter() {
        this.filtered = Object.keys(this.record).reduce((result, key) => {
            switch(key) {
                case "account":
                    return this.record[key] !== "all"
                        ? result.filter(document => document.account.id === this.record[key], [])
                        : result;

                case "startDate":
                    return this.record[key] instanceof BCMSDate
                        ? result.filter(document => document.date.isAfter(this.record[key]), [])
                        : result;

                case "endDate":
                    return this.record[key] instanceof BCMSDate
                        ? result.filter(document => document.date.isBefore(this.record[key]), [])
                        : result;

                default:
                    return result;
            }
        }, Document.sortDateAscending(Document.initAll()));

        return this;
    }

    /**
     * @function init
     * @static
     * @param {OBJECT} values - Values to be passed to the statement search item
     * @returns {StatementSearchItem}
     * @description Function to return an initalized instance
     * @memberof StatementSearchItem
     */
    static init(values) { return new StatementSearchItem(values ?? {}); }
}